import { Injectable } from '@angular/core';

import {
    DEFAULT_RAILING,
    DEFAULT_STAIR_CONFIG,
    IStairCalculationResult,
    IStairConfig,
    railingMaterialsOf,
    risersEnabledOf,
} from '@stair-craft/common/util';

import { calculateLShape } from './calculators/l-shape.calculator';
import { calculateSpiral } from './calculators/spiral.calculator';
import { calculateStraight } from './calculators/straight.calculator';
import { calculateUShape } from './calculators/u-shape.calculator';

function safeNum(value: number, fallback: number): number {
    return Number.isFinite(value) && value > 0 ? value : fallback;
}

function safeNumOrZero(value: number, fallback: number): number {
    return Number.isFinite(value) && value >= 0 ? value : fallback;
}

function safeNullablePositive(value: number | null): number | null {
    return value != null && Number.isFinite(value) && value > 0 ? value : null;
}

function safeNullableNonNegative(value: number | null): number | null {
    return value != null && Number.isFinite(value) && value >= 0 ? value : null;
}

function sanitizeConfig(config: IStairConfig): IStairConfig {
    const d: IStairConfig = DEFAULT_STAIR_CONFIG;

    const c: IStairConfig = {
        ...config,
        roomLength: safeNum(config.roomLength, d.roomLength),
        roomWidth: safeNum(config.roomWidth, d.roomWidth),
        floorHeight: safeNum(config.floorHeight, d.floorHeight),
        floorThickness: safeNum(config.floorThickness, d.floorThickness),
        openingLength: safeNum(config.openingLength, d.openingLength),
        openingWidth: safeNum(config.openingWidth, d.openingWidth),
        openingX: safeNumOrZero(config.openingX, 0),
        openingZ: safeNumOrZero(config.openingZ, 0),
        stairWidth: safeNum(config.stairWidth, d.stairWidth),
        treadThickness: safeNum(config.treadThickness, d.treadThickness),
        noseLength: safeNumOrZero(config.noseLength, d.noseLength),
        flightGap: safeNumOrZero(config.flightGap, d.flightGap),
        stairAngle: safeNullablePositive(config.stairAngle),
        riserHeight: safeNullablePositive(config.riserHeight),
        treadDepth: safeNullablePositive(config.treadDepth),
        stepCount: safeNullablePositive(config.stepCount),
        // turnAtStep === 0 is valid (L/U-shape with no flight 1), so use the
        // non-negative variant — safeNullablePositive would coerce 0 to null
        // and silently send the layout into auto-balance mode.
        turnAtStep: safeNullableNonNegative(config.turnAtStep),
        spiralDiameter: safeNullablePositive(config.spiralDiameter),
        spiralRotation: safeNullablePositive(config.spiralRotation),
        risersEnabled: risersEnabledOf(config),
        ...railingMaterialsOf(config, config.treadMaterialId),
        railing: {
            ...config.railing,
            height: safeNum(config.railing.height, DEFAULT_RAILING.height),
            balusterWidth: safeNum(config.railing.balusterWidth, DEFAULT_RAILING.balusterWidth),
            balusterSpacing: safeNumOrZero(config.railing.balusterSpacing, DEFAULT_RAILING.balusterSpacing),
            handrailWidth: safeNum(config.railing.handrailWidth, DEFAULT_RAILING.handrailWidth),
            handrailHeight: safeNum(config.railing.handrailHeight, DEFAULT_RAILING.handrailHeight),
            postWidth: safeNum(config.railing.postWidth, DEFAULT_RAILING.postWidth),
        },
    };

    return c;
}

@Injectable({ providedIn: 'root' })
export class GeometryEngineService {
    public calculate(config: IStairConfig): IStairCalculationResult {
        const safe: IStairConfig = sanitizeConfig(config);

        switch (safe.type) {
            case 'straight':
                return calculateStraight(safe);

            case 'l-shape':
                return calculateLShape(safe);

            case 'u-shape':
                return calculateUShape(safe);

            case 'spiral':
                return calculateSpiral(safe);

            default:
                throw new Error(`Unknown stair type: "${config.type}"`);
        }
    }
}
